import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useSupabase } from '../contexts/SupabaseContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { MessageCircle, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const ThreadChat = ({ conversationId, friendId, friendName, friendAvatar, onClose }) => {
  const { user } = useContext(AuthContext);
  const { 
    sendMessage: sendMessageToSupabase, 
    fetchMessages, 
    markAsRead,
    subscribeToConversation,
    unsubscribe 
  } = useSupabase();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Auto-focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fetch messages on mount
  useEffect(() => {
    if (conversationId) {
      loadMessages();
    }
  }, [conversationId]);

  // Subscribe to real-time messages
  useEffect(() => {
    if (!conversationId) return;

    const channel = subscribeToConversation(conversationId, (newMsg) => {
      // Add new message if not from current user (own messages added optimistically)
      if (newMsg.sender_id !== user?.id) {
        setMessages(prev => {
          const exists = prev.some(m => m.id === newMsg.id);
          if (exists) return prev;
          return [...prev, newMsg];
        });
        // Mark as read
        markAsRead(conversationId, user?.id);
      }
    });

    return () => {
      if (channel) unsubscribe(channel);
    };
  }, [conversationId, user?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const isTyping = document.activeElement?.tagName === 'TEXTAREA' || 
                     document.activeElement?.tagName === 'INPUT';
    
    if (window.innerWidth <= 768 && isTyping) {
      return;
    }
    
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadMessages = async () => {
    setLoading(true);
    const result = await fetchMessages(conversationId);
    
    if (result.success) {
      setMessages(result.data);
      // Mark messages as read
      markAsRead(conversationId, user?.id);
    }
    
    setLoading(false);
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    const messageText = newMessage.trim();
    if (!messageText || sending) return;

    setSending(true);

    // Optimistically add message
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: user.id,
      message: messageText,
      created_at: new Date().toISOString(),
      sender: {
        id: user.id,
        name: user.full_name,
        avatar_url: user.avatar_url
      }
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');

    const result = await sendMessageToSupabase(conversationId, user, messageText);

    if (result.success) {
      // Replace temp message with real one
      setMessages(prev => prev.map(m => 
        m.id === tempId ? { ...result.data, sender: optimisticMessage.sender } : m
      ));
    } else {
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(messageText); // Restore message
    }

    setSending(false);
    // Always refocus after sending (desktop + mobile)
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex flex-col overflow-hidden friends-chat-container z-50"
      style={{ touchAction: 'none' }}
    >
      {/* Header - Fixed */}
      <div className="border-b border-gray-200 p-4 bg-white flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <Avatar className="w-10 h-10">
            {friendAvatar ? (
              <img 
                src={friendAvatar} 
                alt={friendName}
                className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
            ) : (
              <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white font-semibold">
                {friendName?.charAt(0).toUpperCase() || 'F'}
              </AvatarFallback>
            )}
          </Avatar>
          
          <div>
            <h3 className="font-semibold text-gray-900">{friendName || 'Chat'}</h3>
            <p className="text-xs text-gray-500">Friend</p>
          </div>
        </div>
      </div>

      {/* Messages Area - Scrollable */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 pt-4 pb-24 space-y-4 min-h-0"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y'
        }}
      >
        <div className="max-w-4xl mx-auto pt-2">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.sender_id === user?.id;
              
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}
                >
                  <div className={`flex items-end space-x-2 max-w-[80%] ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    {/* Avatar */}
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      {isOwn ? (
                        user?.avatar_url ? (
                          <img 
                            src={user.avatar_url} 
                            alt={user.full_name}
                            className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                        ) : (
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs font-semibold">
                            {user?.full_name?.charAt(0).toUpperCase() || 'Y'}
                          </AvatarFallback>
                        )
                      ) : (
                        message.sender?.avatar_url ? (
                          <img 
                            src={message.sender.avatar_url} 
                            alt={message.sender.name}
                            className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                        ) : (
                          <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white text-xs font-semibold">
                            {message.sender?.name?.charAt(0).toUpperCase() || friendName?.charAt(0).toUpperCase() || 'F'}
                          </AvatarFallback>
                        )
                      )}
                    </Avatar>

                    {/* Message Bubble */}
                    <div className={`rounded-2xl px-4 py-2 ${
                      isOwn 
                        ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white' 
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}>
                      {/* Sender name for non-own messages */}
                      {!isOwn && (
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {message.sender?.name || friendName}
                        </p>
                      )}

                      {/* Message content */}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {message.message}
                      </p>

                      {/* Timestamp and Read Receipt */}
                      <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-orange-100' : 'text-gray-400'}`}>
                        <span className="text-xs">{formatTime(message.created_at)}</span>
                        {isOwn && (
                          <span className={`text-xs ${message.read_at ? 'text-blue-400' : ''}`}>
                            {message.read_at ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input - Fixed at bottom */}
      <div className="border-t border-gray-200 bg-white p-4 flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1"
              disabled={sending}
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ThreadChat;
